import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MDBAlert, MDBBtn, MDBCard, MDBCardBody, MDBIcon, MDBSpinner } from 'mdb-react-ui-kit';
import { BOOTH_STATUS, HOLD_MINUTES, boothKeyOf, formatCountdown, holdRemainingSeconds } from '../../../../utils/boothMap';
import { boothImageUrl, getBoothAssetUrl, peekBoothAssetUrl } from '../../../../utils/boothAssets';
import { getLocalizedText } from '../../../../utils/localizedText';
import { transformMoney } from '../../../../utils/money';
import { captureError } from '../../../../utils/sentryHelpers';
import {
  bookingClosedReason,
  formatArea,
  formatLength,
  getStatusLabel,
  isOwnStatus,
  positionTypeLabel,
  priceBreakdownLines,
  statusVariant,
} from './boothStatusText';
import { priceBreakdownOf, variantInfoOf } from './boothMapView';

const Booth3DView = lazy(() => import('../../../../components/booth-booking/Booth3DView'));

// "drag to rotate" hint with the intro turn: shown for the first HINT_MAX booths with a 3D model, then only the legend
const HINT_STORAGE_KEY = 'boothBooking.3dHintCount';
const HINT_MAX = 2;
const HINT_MS = 4500;

function readHintCount() {
  try {
    return Number(window.localStorage.getItem(HINT_STORAGE_KEY)) || 0;
  } catch (e) {
    return HINT_MAX;
  }
}

function countHintShown() {
  try {
    window.localStorage.setItem(HINT_STORAGE_KEY, String(readHintCount() + 1));
  } catch (e) {
    // without storage the hint simply shows again next time
  }
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Spinner() {
  const { t } = useTranslation();
  return (
    <div className="d-flex justify-content-center align-items-center py-5">
      <MDBSpinner role="status" size="sm">
        <span className="visually-hidden">{t('Loading...')}</span>
      </MDBSpinner>
    </div>
  );
}

/**
 * Signed URL of a stored booth asset, loaded only while `active`.
 * Only a settled request (`done`) counts as loaded: a request cancelled because the view was left
 * (or the path changed) is simply started again on the next activation.
 */
function useAssetUrl(file, active, operation) {
  const s3Path = file && file.s3Path ? file.s3Path : '';
  const [state, setState] = useState({ s3Path: '', url: '', failed: false, done: false });

  useEffect(() => {
    if (!active || !s3Path) return undefined;
    if (state.s3Path === s3Path && state.done) return undefined;
    let cancelled = false;
    setState({ s3Path, url: '', failed: false, done: false });
    getBoothAssetUrl(s3Path)
      .then((url) => {
        if (!cancelled) setState({ s3Path, url: url || '', failed: !url, done: true });
      })
      .catch((error) => {
        captureError(error, {
          feature: 'booth-booking',
          operation,
          consoleMessage: 'Loading a booth asset URL failed',
          context: { s3Path },
        });
        if (!cancelled) setState({ s3Path, url: '', failed: true, done: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, s3Path]);

  if (state.s3Path !== s3Path || !state.done) {
    // a URL signed earlier in this session (booth visited before) is there at once: no extra render without it
    const known = active && s3Path ? peekBoothAssetUrl(s3Path) : '';
    if (known) return { url: known, loading: false, failed: false };
    return { url: '', loading: !!(active && s3Path), failed: false };
  }
  return { url: state.url, loading: false, failed: state.failed };
}

/**
 * All bullet lines of Product.includes for the current language. The field is stored as one
 * {language, text} entry per bullet (and an entry may itself contain several lines), so every
 * matching entry is rendered, falling back to German when nothing matches the current language.
 */
export function getIncludeLines(includes, language) {
  const entries = (Array.isArray(includes) ? includes : []).filter((entry) => entry && typeof entry.text === 'string');
  const lang = String(language || '').toLowerCase();
  const baseLang = lang.split('-')[0];
  const byLanguage = (code) => entries.filter((entry) => String(entry.language || '').toLowerCase() === code);
  let selected = byLanguage(lang);
  if (selected.length === 0 && baseLang && baseLang !== lang) selected = byLanguage(baseLang);
  if (selected.length === 0) selected = byLanguage('de');
  return selected.flatMap((entry) =>
    entry.text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  );
}

/** Component products (sorted, with quantity) and include lines of the booth product. */
function packageItemsOf(product, language) {
  if (!product) return { components: [], includeLines: [] };
  const components = [...((product.components && product.components.items) || [])]
    .filter((c) => c && c.componentProduct)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  return { components, includeLines: getIncludeLines(product.includes, language) };
}

function Countdown({ expiresAt, nowMs }) {
  const seconds = holdRemainingSeconds(expiresAt, nowMs);
  return (
    <span className="font-monospace fw-bold">
      <MDBIcon far icon="clock" className="me-1" />
      {formatCountdown(seconds)}
    </span>
  );
}

function StatusChip({ status, bookingOpen }) {
  const { t } = useTranslation();
  return <span className={`booth-status-chip booth-status-chip--${statusVariant(status)}`}>{getStatusLabel(t, status, bookingOpen)}</span>;
}

/** Short reason next to a button disabled because booth booking is closed; nothing without a reason. */
function ClosedReason({ reason }) {
  if (!reason) return null;
  return (
    <p className="small text-muted mb-0" data-testid="booth-booking-closed-reason">
      <MDBIcon fas icon="lock" className="me-1" />
      {reason}
    </p>
  );
}

function CountTile({ value, label, variant }) {
  return (
    <div className="flex-fill text-center border rounded-3 py-2 px-1">
      <div className="h4 mb-0 fw-bold">{value ?? 0}</div>
      <div className="small text-muted">
        <span className={`booth-legend-swatch booth-legend-swatch--${variant} me-1`} />
        {label}
      </div>
    </div>
  );
}

/**
 * "Continue to checkout" with the number of own booths that go into the checkout URL (plural keys
 * "Continue to checkout ({{count}} booths)_one" / "_other"). Disabled while booth booking is closed
 * (switch off or outside the booking window); `closedReason` then says why next to the button.
 */
function CheckoutButton({ checkoutUrl, count, disabled, closedReason, large }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      <MDBBtn
        color="primary"
        block
        size={large ? 'lg' : undefined}
        className={large ? 'booth-details-panel__cta text-none' : undefined}
        disabled={disabled}
        onClick={() => navigate(checkoutUrl)}
      >
        {count > 0 ? t('Continue to checkout ({{count}} booths)', { count }) : t('Continue to checkout')}
        <MDBIcon fas icon="arrow-right" className="ms-2" />
      </MDBBtn>
      <ClosedReason reason={closedReason} />
    </>
  );
}

function HeldBoothsList({ heldBooths, nowMs, checkoutUrl, checkoutCount, onSelectBooth, showCheckout, bookingOpen, closedReason }) {
  const { t } = useTranslation();
  if (!heldBooths || heldBooths.length === 0) return null;
  return (
    <div className="mt-2">
      <h6 className="fw-bold mb-2">{t('Your reserved booths')}</h6>
      <ul className="list-unstyled mb-3">
        {heldBooths.map((booth) => (
          <li key={boothKeyOf(booth)} className="d-flex justify-content-between align-items-center py-1 border-bottom">
            <MDBBtn color="link" size="sm" className="text-none p-0" onClick={() => onSelectBooth && onSelectBooth(booth)}>
              {t('Booth {{number}}', { number: booth.boothNumber })}
            </MDBBtn>
            {booth.status === BOOTH_STATUS.HELD_BY_YOU && booth.expiresAt ? (
              <Countdown expiresAt={booth.expiresAt} nowMs={nowMs} />
            ) : (
              <span className="small text-muted">{getStatusLabel(t, booth.status)}</span>
            )}
          </li>
        ))}
      </ul>
      {showCheckout && (
        <CheckoutButton checkoutUrl={checkoutUrl} count={checkoutCount} disabled={!bookingOpen} closedReason={bookingOpen ? '' : closedReason} />
      )}
    </div>
  );
}

/** Size, dimensions, position type and zone of the booth as small tiles; only the facts the booth carries. */
function FactTiles({ booth }) {
  const { t } = useTranslation();
  const position = positionTypeLabel(t, booth.positionType);
  const zone = booth.zone === null || booth.zone === undefined ? '' : String(booth.zone).trim();
  const facts = [];
  if (booth.width || booth.depth) facts.push([t('Dimensions'), `${formatLength(booth.width)} × ${formatLength(booth.depth)} m`]);
  facts.push([t('Size'), `${formatArea(booth.area)} m²`]);
  if (position) facts.push([t('Position'), position]);
  if (zone) facts.push([t('Zone'), zone]);
  return (
    <div className="booth-details-panel__facts" data-testid="booth-facts">
      {facts.map(([label, value]) => (
        <div key={label} className="booth-details-panel__fact">
          <span>{label}</span>
          <b title={value}>{value}</b>
        </div>
      ))}
    </div>
  );
}

/** The included package as one row; opens the package sheet over the panel (the card keeps its height). */
function PackageRow({ productDetails, onOpen }) {
  const { t, i18n } = useTranslation();
  if (!productDetails || productDetails.loading) {
    return (
      <div className="booth-details-panel__package-row">
        <span className="booth-details-panel__package-icon bg-primary text-white">
          <MDBSpinner size="sm" role="status">
            <span className="visually-hidden">{t('Loading...')}</span>
          </MDBSpinner>
        </span>
        <span className="booth-details-panel__package-text">
          <b>{t('Included package')}</b>
        </span>
      </div>
    );
  }
  if (productDetails.error) return <p className="small text-danger mb-0">{t('The included package could not be loaded.')}</p>;
  const { components, includeLines } = packageItemsOf(productDetails.product, i18n && i18n.language);
  const count = components.length + includeLines.length;
  if (count === 0) return null;
  return (
    <button type="button" className="booth-details-panel__package-row" onClick={onOpen}>
      <span className="booth-details-panel__package-icon bg-primary text-white">
        <MDBIcon fas icon="box-open" />
      </span>
      <span className="booth-details-panel__package-text">
        <b>{t('Included package')}</b>
        {/* plural keys "{{count}} items_one" / "_other" */}
        <span>{t('{{count}} items', { count })}</span>
      </span>
      <MDBIcon fas icon="chevron-right" className="text-muted" />
    </button>
  );
}

/**
 * Included package of the selected booth as a sheet over the whole panel: back button, components and include
 * lines, and the booking action at the bottom. Escape closes it.
 */
function PackageSheet({ booth, productDetails, onClose, children }) {
  const { t, i18n } = useTranslation();
  const backRef = useRef(null);
  const { components, includeLines } = packageItemsOf(productDetails && productDetails.product, i18n && i18n.language);
  const grouped = components.length > 0 && includeLines.length > 0;

  useEffect(() => {
    if (backRef.current) backRef.current.focus();
  }, []);

  return (
    <div
      className="booth-package-sheet"
      role="dialog"
      aria-label={t('Included package')}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="booth-package-sheet__head">
        <button ref={backRef} type="button" className="booth-package-sheet__back" aria-label={t('Back')} onClick={onClose}>
          <MDBIcon fas icon="chevron-left" />
        </button>
        <div style={{ minWidth: 0 }}>
          <div className="fw-bold">{t('Included package')}</div>
          <div className="small text-muted">
            {t('Booth {{number}}', { number: booth.boothNumber })}
            {booth.price === null || booth.price === undefined ? '' : ` · ${transformMoney(booth.price)} ${t('plus VAT')}`}
          </div>
        </div>
      </div>
      <div className="booth-package-sheet__body">
        {components.length > 0 ? (
          <div>
            {grouped ? <h6 className="booth-package-sheet__group">{t('Equipment')}</h6> : null}
            <ul className="booth-package-sheet__list">
              {components.map((component, index) => (
                <li key={`c-${component.componentProduct.id}-${index}`}>
                  <span className="booth-package-sheet__qty">{component.qty || 1} ×</span>
                  <span>{getLocalizedText(component.componentProduct.name)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {includeLines.length > 0 ? (
          <div>
            {grouped ? <h6 className="booth-package-sheet__group">{t('Included')}</h6> : null}
            <ul className="booth-package-sheet__list">
              {includeLines.map((line, index) => (
                <li key={`i-${index}`}>
                  <MDBIcon fas icon="check" className="text-success booth-package-sheet__qty" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {children ? <div className="booth-package-sheet__foot">{children}</div> : null}
    </div>
  );
}

/**
 * Media stage of one booth. Rendered with key={booth.productId}: the media belong to the product.
 *
 * The image shows at once; a stored 3D model loads in the background (signed URL, then the GLB in a hidden
 * Booth3DView) and fades in when it is on screen, with a short turn and a "drag to rotate" hint for the first
 * booths. A view the user picked before the model was ready is kept (the caption then offers the 3D view).
 * Without an image the 3D view (model or procedural booth) is the stage. The dimension drawing PDF loads on demand.
 */
function BoothStage({ booth, overlay }) {
  const { t } = useTranslation();
  const imageUrl = booth.image && booth.image.fileName ? boothImageUrl(booth.image.fileName) : '';
  const hasModel = !!(booth.model3d && booth.model3d.s3Path);
  const hasPdf = !!(booth.dimensionFile && booth.dimensionFile.s3Path);

  const [mode, setMode] = useState(imageUrl ? 'image' : '3d');
  const [userPicked, setUserPicked] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [intro, setIntro] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);
  const [freshCaption, setFreshCaption] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  // enlarged view: the same stage (3D view keeps its state) as a dialog over almost the whole window
  const [expanded, setExpanded] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const stageRef = useRef(null);
  const expandBtnRef = useRef(null);
  const closeBtnRef = useRef(null);

  const model =useAssetUrl(booth.model3d, hasModel, 'load-booth-3d-model');
  const pdf = useAssetUrl(booth.dimensionFile, mode === 'plan', 'load-booth-dimension-file');

  const modelFailed = failed || model.failed;
  // 3D: the stored model; the procedural booth only stands in when there is no image either
  const offer3d = hasModel ? !modelFailed || !imageUrl : !imageUrl;
  const mount3d = offer3d && (!hasModel || !!model.url || model.failed);
  const modelUrl = hasModel && !model.failed ? model.url : '';
  const show3d = mode === '3d' && (ready || failed);

  useEffect(() => {
    if (!ready) return undefined;
    if (userPicked && mode !== '3d') return undefined;
    if (!userPicked) setMode('3d');
    setFreshCaption(true);
    if (readHintCount() >= HINT_MAX || prefersReducedMotion()) return undefined;
    countHintShown();
    setIntro(true);
    setHintVisible(true);
    const timer = setTimeout(() => setHintVisible(false), HINT_MS);
    return () => clearTimeout(timer);
    // only when the model becomes ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // a model that fails after the user switched to it falls back to the image
  useEffect(() => {
    if (modelFailed && imageUrl && mode === '3d') setMode('image');
  }, [modelFailed, imageUrl, mode]);

  const handleReady = useCallback(() => setReady(true), []);
  const handleError = useCallback(() => setFailed(true), []);
  const handleInteract = useCallback(() => setHintVisible(false), []);

  const pick = (next) => {
    setUserPicked(true);
    setMode(next);
  };

  const openExpanded = () => {
    // the card keeps the stage's height while the stage is lifted out of it
    setPlaceholderHeight(stageRef.current ? stageRef.current.offsetHeight : 0);
    setExpanded(true);
  };

  const closeExpanded = useCallback(() => {
    setExpanded(false);
    setPlaceholderHeight(0);
    // back to the button that opened it
    setTimeout(() => expandBtnRef.current && expandBtnRef.current.focus(), 0);
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeExpanded();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    if (closeBtnRef.current) closeBtnRef.current.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [expanded, closeExpanded]);

  const canExpand = !!imageUrl || offer3d || hasPdf;

  const options = [];
  if (imageUrl) options.push({ key: 'image', label: t('Image') });
  if (offer3d) options.push({ key: '3d', label: t('3D'), loading: hasModel && !ready && !modelFailed });
  if (hasPdf) options.push({ key: 'plan', label: t('Dimensions') });

  let caption = null;
  if (mode === 'plan') {
    caption = (
      <>
        <span className="booth-stage__caption-text">{t('Dimension drawing')}</span>
        {pdf.url ? (
          <a href={pdf.url} target="_blank" rel="noopener noreferrer" className="booth-stage__caption-btn">
            {t('Open PDF')}
            <MDBIcon fas icon="external-link-alt" className="ms-1" />
          </a>
        ) : null}
      </>
    );
  } else if (mode === '3d' && ready) {
    caption = (
      <>
        <span className="booth-stage__caption-text" data-testid="booth-3d-legend">
          <span className="text-nowrap">
            <kbd>{t('Drag')}</kbd> {t('rotate')}
          </span>
          <span className="text-nowrap">
            <kbd>Shift</kbd> + <kbd>{t('Drag')}</kbd> {t('move')}
          </span>
          <span className="text-nowrap">
            <kbd>{t('Wheel')}</kbd> {t('zoom')}
          </span>
        </span>
        <button
          type="button"
          className="booth-stage__caption-btn"
          title={t('Reset view')}
          aria-label={t('Reset view')}
          onClick={() => setResetSignal((n) => n + 1)}
        >
          <MDBIcon fas icon="undo" />
        </button>
      </>
    );
  } else if (hasModel && offer3d && !ready && !modelFailed) {
    caption = (
      <span className="booth-stage__caption-text">
        <MDBSpinner size="sm" className="booth-stage__caption-spinner" role="status" />
        {t('3D view is loading')}
        {progress > 0 && progress < 100 ? <span className="font-monospace opacity-75">{Math.round(progress)} %</span> : null}
      </span>
    );
  } else if (mode === 'image' && ready) {
    caption = (
      <>
        <span className="booth-stage__caption-text">{t('3D view ready')}</span>
        <button type="button" className="booth-stage__caption-btn" onClick={() => pick('3d')}>
          {t('View in 3D')}
          <MDBIcon fas icon="chevron-right" className="ms-1" />
        </button>
      </>
    );
  }

  const boothLabel = t('Booth {{number}}', { number: booth.boothNumber });

  const stage = (
    <div
      ref={stageRef}
      className={`booth-stage${expanded ? ' booth-stage--expanded' : ''}`}
      role={expanded ? 'dialog' : undefined}
      aria-modal={expanded ? 'true' : undefined}
      aria-label={expanded ? boothLabel : undefined}
    >
      <div className="booth-stage__media">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={(booth.image && booth.image.alt) || boothLabel}
            className={`booth-stage__layer booth-stage__image${mode === 'image' ? '' : ' booth-stage__layer--hidden'}${
              expanded ? '' : ' booth-stage__image--zoomable'
            }`}
            onClick={expanded ? undefined : openExpanded}
          />
        ) : null}
        {!imageUrl && !offer3d ? (
          <div className="booth-stage__layer booth-3d-view__message">
            <MDBIcon far icon="image" size="2x" className="text-muted mb-2" />
            <span className="small text-muted">{t('No image available')}</span>
          </div>
        ) : null}
        {mount3d ? (
          <div className={`booth-stage__layer${show3d ? '' : ' booth-stage__layer--hidden'}`} aria-hidden={!show3d}>
            <Suspense fallback={null}>
              <Booth3DView
                className="booth-stage__3d"
                modelUrl={modelUrl || undefined}
                width={booth.width}
                depth={booth.depth}
                height={booth.height}
                onReady={handleReady}
                onProgress={setProgress}
                onError={handleError}
                onInteract={handleInteract}
                introSpin={intro && show3d}
                resetSignal={resetSignal}
              />
            </Suspense>
            <div className={`booth-stage__hint${hintVisible && show3d ? ' booth-stage__hint--visible' : ''}`} aria-hidden="true">
              <span className="booth-stage__hint-gesture">
                <span />
              </span>
              {t('Drag to rotate')}
              <small>{t('Shift + drag moves · wheel zooms')}</small>
            </div>
          </div>
        ) : null}
        {mode === '3d' && !ready && !failed ? (
          <div className="booth-stage__layer booth-stage__loading">
            <MDBSpinner role="status" size="sm">
              <span className="visually-hidden">{t('Loading...')}</span>
            </MDBSpinner>
          </div>
        ) : null}
        {mode === 'plan' ? (
          <div className="booth-stage__layer booth-stage__plan">
            {pdf.loading ? (
              <Spinner />
            ) : pdf.url ? (
              <iframe src={pdf.url} title={t('Dimension drawing')} />
            ) : (
              <p className="small text-danger p-3 mb-0">{t('The file could not be loaded.')}</p>
            )}
          </div>
        ) : null}

        {expanded ? (
          <div className="booth-stage__overlay">
            <span className="booth-stage__title">{boothLabel}</span>
            <button
              ref={closeBtnRef}
              type="button"
              className="booth-stage__close"
              title={t('Close')}
              aria-label={t('Close')}
              onClick={closeExpanded}
            >
              <MDBIcon fas icon="times" />
            </button>
          </div>
        ) : overlay ? (
          <div className="booth-stage__overlay">{overlay}</div>
        ) : null}

        {canExpand ? (
          <button
            ref={expandBtnRef}
            type="button"
            className="booth-stage__expand"
            title={expanded ? t('Reduce view') : t('Enlarge view')}
            aria-label={expanded ? t('Reduce view') : t('Enlarge view')}
            onClick={expanded ? closeExpanded : openExpanded}
          >
            <MDBIcon fas icon={expanded ? 'compress' : 'expand'} />
          </button>
        ) : null}

        {options.length > 1 ? (
          <div className="booth-stage__switch" role="group" aria-label={t('View')}>
            {options.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={mode === option.key}
                className={option.key === '3d' && freshCaption ? 'booth-stage__switch--fresh' : undefined}
                onClick={() => pick(option.key)}
              >
                {option.loading ? <span className="booth-stage__switch-spinner" /> : null}
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {caption ? (
        <div className={`booth-stage__caption${freshCaption ? ' booth-stage__caption--fresh' : ''}`} onAnimationEnd={() => setFreshCaption(false)}>
          {caption}
        </div>
      ) : null}
    </div>
  );

  // same element tree in both states: the stage (and its WebGL canvas) is not remounted when enlarged
  return (
    <div style={expanded && placeholderHeight ? { height: placeholderHeight } : undefined}>
      {expanded ? <div className="booth-stage-backdrop" aria-hidden="true" onClick={closeExpanded} /> : null}
      {stage}
    </div>
  );
}

/**
 * Status-dependent actions of the selected booth. "Book booth" holds the booth and continues to the checkout
 * (onBook). While booth booking is closed (`bookingOpen` false) booking and the checkout are disabled with
 * `closedReason` next to them, and the hold-duration hint of a free booth is hidden; releasing an own hold stays
 * possible. `compact` (package sheet) leaves out the hints and the release link.
 * No waiting-list actions: the waiting list is switched off for now (product decision 2026-10-14).
 */
function BoothActions({ booth, nowMs, checkoutUrl, checkoutCount, busy, bookingOpen, closedReason, onBook, onRelease, compact }) {
  const { t } = useTranslation();
  const reason = bookingOpen ? '' : closedReason;
  switch (booth.status) {
    case BOOTH_STATUS.FREE:
      return (
        <div className="d-grid gap-2">
          <MDBBtn color="primary" size="lg" block className="booth-details-panel__cta text-none" disabled={busy || !bookingOpen} onClick={() => onBook(booth)}>
            {busy ? <MDBSpinner size="sm" className="me-2" /> : !bookingOpen ? <MDBIcon fas icon="lock" className="me-2" /> : null}
            {t('Book booth')}
            {bookingOpen && !busy ? <MDBIcon fas icon="arrow-right" className="ms-2" /> : null}
          </MDBBtn>
          {compact ? null : bookingOpen ? (
            <p className="small text-muted mb-0">
              <MDBIcon far icon="clock" className="me-1" />
              {t('The booth is held for {{minutes}} minutes while you complete the booking.', { minutes: HOLD_MINUTES })}
            </p>
          ) : (
            <ClosedReason reason={reason} />
          )}
        </div>
      );
    case BOOTH_STATUS.HELD_BY_YOU:
      return (
        <div className="d-grid gap-2">
          <div className="booth-hold-banner">
            <span>{t('Hold expires in')}</span>
            {booth.expiresAt ? <Countdown expiresAt={booth.expiresAt} nowMs={nowMs} /> : null}
          </div>
          <CheckoutButton checkoutUrl={checkoutUrl} count={checkoutCount} disabled={busy || !bookingOpen} closedReason={reason} large />
          {compact ? null : (
            <MDBBtn color="link" block size="sm" className="text-none" disabled={busy} onClick={() => onRelease(booth)}>
              {t('Release hold')}
            </MDBBtn>
          )}
        </div>
      );
    case BOOTH_STATUS.RESERVED_FOR_YOU:
      return (
        <div className="d-grid gap-2">
          {compact ? null : <p className="small text-muted mb-0">{t('This booth has been reserved for your company.')}</p>}
          <CheckoutButton checkoutUrl={checkoutUrl} count={checkoutCount} disabled={busy || !bookingOpen} closedReason={reason} large />
        </div>
      );
    case BOOTH_STATUS.HELD_BY_OTHER:
    case BOOTH_STATUS.RESERVED_FOR_OTHER:
      return (
        <p className="small text-muted mb-0">
          <MDBIcon fas icon="hourglass-half" className="me-2" />
          {t('Another exhibitor currently holds or has an option on this booth.')}
        </p>
      );
    case BOOTH_STATUS.SOLD:
      return (
        <p className="small text-muted mb-0">
          <MDBIcon fas icon="lock" className="me-2" />
          {booth.exhibitor && booth.exhibitor.name ? t('Sold to {{company}}', { company: booth.exhibitor.name }) : t('Sold')}
        </p>
      );
    default:
      return (
        <p className="small text-muted mb-0">
          <MDBIcon fas icon="ban" className="me-2" />
          {booth.bookable === false ? t('Not bookable') : t('This booth is currently not bookable.')}
        </p>
      );
  }
}

/**
 * Right column of the hall-plan booking page: overview (no selection) or the selected booth with the media
 * stage, price, the booking action, fact tiles and the included package (as a sheet over the panel).
 * onBook holds the booth and continues to the checkout; without onBook the button only calls onReserve.
 */
export default function BoothDetailsPanel({
  booth,
  counts,
  variants,
  products,
  productDetails,
  onClear,
  onSelectBooth,
  onBook,
  onReserve,
  onRelease,
  heldBooths,
  checkoutUrl,
  busy,
  bookingOpen,
  bookingWindow,
  nowMs,
  notice,
  onDismissNotice,
}) {
  const { t } = useTranslation();
  const [packageOpen, setPackageOpen] = useState(false);
  const boothKey = booth ? boothKeyOf(booth) : null;
  const safeCounts = counts || {};
  // every own booth (held or optioned) goes into the checkout URL
  const checkoutCount = (heldBooths || []).length;
  // why booking / checkout are disabled (boothBookingWindow of the page; generic text without it)
  const closedReason = bookingOpen ? '' : bookingClosedReason(t, bookingWindow);

  useEffect(() => {
    setPackageOpen(false);
  }, [boothKey]);

  const noticeAlert = notice ? (
    <MDBAlert open color={notice.color || 'info'} className="small py-2 px-3 mb-0 d-flex justify-content-between align-items-start">
      <span>{notice.message}</span>
      <button type="button" className="btn-close btn-sm ms-2" aria-label={t('Close')} onClick={onDismissNotice} />
    </MDBAlert>
  ) : null;

  if (!booth) {
    return (
      <MDBCard className="booth-details-panel">
        <MDBCardBody>
          {noticeAlert ? <div className="mb-3">{noticeAlert}</div> : null}
          <h5 className="fw-bold">{t('Choose a booth on the hall plan')}</h5>
          <p className="small text-muted">{t('Click a booth on the hall plan to see its size, price and included package.')}</p>
          <div className="d-flex gap-2 mb-3">
            <CountTile value={safeCounts.free} label={t('Available')} variant="free" />
            <CountTile value={safeCounts.reserved} label={t('Reserved')} variant="reserved" />
            <CountTile value={safeCounts.sold} label={t('Sold')} variant="sold" />
          </div>
          <HeldBoothsList
            heldBooths={heldBooths}
            nowMs={nowMs}
            checkoutUrl={checkoutUrl}
            checkoutCount={checkoutCount}
            onSelectBooth={onSelectBooth}
            showCheckout
            bookingOpen={bookingOpen}
            closedReason={closedReason}
          />
        </MDBCardBody>
      </MDBCard>
    );
  }

  const name = getLocalizedText(booth.name);
  const variantInfo = variantInfoOf(booth, variants, products);
  // the product name mostly carries the offer code already ("Ausstellungsstand 18 m² (18B)")
  const offerLabel = variantInfo && !(name || '').includes(variantInfo.variant) ? t('Offer {{variant}}', { variant: variantInfo.variant }) : '';
  const kicker = [name, offerLabel].filter(Boolean).join(' · ');
  // booth.price is the price of THIS booth (map: priceForBooth of the primary offer); the breakdown explains it
  const priceLines = priceBreakdownLines(t, priceBreakdownOf(booth.priceDetails), transformMoney);
  const hasPrice = booth.price !== null && booth.price !== undefined;

  const actionProps = {
    booth,
    nowMs,
    checkoutUrl,
    checkoutCount,
    busy,
    bookingOpen: !!bookingOpen,
    closedReason,
    onBook: onBook || onReserve,
    onRelease,
  };

  return (
    <MDBCard className="booth-details-panel">
      <BoothStage
        key={booth.productId || booth.eventProductId}
        booth={booth}
        overlay={
          <>
            <StatusChip status={booth.status} bookingOpen={!!bookingOpen} />
            <button type="button" className="booth-stage__close" title={t('Close')} aria-label={t('Close')} onClick={onClear}>
              <MDBIcon fas icon="times" />
            </button>
          </>
        }
      />
      <MDBCardBody className="booth-details-panel__body">
        {noticeAlert}
        <div>
          {kicker ? (
            <div className="booth-details-panel__kicker" data-testid="booth-kicker">
              {kicker}
            </div>
          ) : null}
          <h2 className="booth-details-panel__title">{t('Booth {{number}}', { number: booth.boothNumber })}</h2>
        </div>

        <div>
          {hasPrice ? (
            <div className="d-flex flex-wrap align-items-baseline gap-2">
              <span className="booth-details-panel__amount">{transformMoney(booth.price)}</span>
              <span className="small text-muted">{t('plus VAT')}</span>
            </div>
          ) : (
            <span className="booth-details-panel__amount">–</span>
          )}
          {priceLines.map((line, index) => (
            <div key={`price-line-${index}`} className="small text-muted" data-testid="booth-price-breakdown">
              {line}
            </div>
          ))}
        </div>

        <BoothActions {...actionProps} />

        <FactTiles booth={booth} />

        <PackageRow productDetails={productDetails} onOpen={() => setPackageOpen(true)} />

        {/* An own selected booth already shows the checkout button in BoothActions. */}
        <HeldBoothsList
          heldBooths={(heldBooths || []).filter((b) => boothKeyOf(b) !== boothKey)}
          nowMs={nowMs}
          checkoutUrl={checkoutUrl}
          checkoutCount={checkoutCount}
          onSelectBooth={onSelectBooth}
          showCheckout={!isOwnStatus(booth.status)}
          bookingOpen={bookingOpen}
          closedReason={closedReason}
        />
      </MDBCardBody>
      {packageOpen ? (
        <PackageSheet booth={booth} productDetails={productDetails} onClose={() => setPackageOpen(false)}>
          {booth.status === BOOTH_STATUS.FREE || isOwnStatus(booth.status) ? <BoothActions {...actionProps} compact /> : null}
        </PackageSheet>
      ) : null}
    </MDBCard>
  );
}
